import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { 
  FileText,
  Building,
  Edit,
  Eye,
  Trash2,
  ExternalLink,
  Plus,
  TrendingUp,
  Activity
} from 'lucide-react';
import enhancedBlogService from '../services/enhancedBlogService';
import { useAuth } from '../contexts/AuthContext';
import DeleteBlog from '../components/DeleteBlog';



interface BlogData {
  id: number;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  publishedAt: string | null;
  views: number;
  likes: number;
  createdAt: string;
  updatedAt: string;
}

interface CaseStudyData {
  id: number;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  publishedAt: string | null;
  views: number;
  createdAt: string;
  updatedAt: string;
}

const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const [blogs, setBlogs] = useState<BlogData[]>([]);
  const [caseStudies, setCaseStudies] = useState<CaseStudyData[]>([]);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showCaseStudies, setShowCaseStudies] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    blogId: number | null;
    blogTitle: string;
  }>({
    isOpen: false,
    blogId: null,
    blogTitle: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const loadUserContent = async () => {
    console.log('Dashboard: loadUserContent called, user:', user);
    
    setIsLoadingContent(true);
    try {
      // Load user's blogs and case studies
      const blogsResponse = await enhancedBlogService.getUserBlogs();
      const caseStudiesResponse = await enhancedBlogService.getUserCaseStudies();
      
      setBlogs(blogsResponse.blogs || []);
      setCaseStudies(caseStudiesResponse.caseStudies || []);
    } catch (error) {
      console.error('Error loading user content:', error);
    } finally {
      setIsLoadingContent(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserContent();
    }
  }, [user]);



  const handlePublishBlog = async (blogId: number) => {
    try {
      await enhancedBlogService.updateBlogStatus(blogId, 'published');
      loadUserContent(); // Reload content
    } catch (error) {
      console.error('Error publishing blog:', error);
    }
  };

  const handlePublishCaseStudy = async (caseStudyId: number) => {
    try {
      await enhancedBlogService.updateCaseStudyStatus(caseStudyId, 'published');
      loadUserContent(); // Reload content
    } catch (error) {
      console.error('Error publishing case study:', error);
    }
  };

  const handleDeleteBlog = async (blogId: number, blogTitle: string) => {
    setDeleteModal({
      isOpen: true,
      blogId,
      blogTitle
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.blogId) {
      try {
        await enhancedBlogService.deleteBlog(deleteModal.blogId);
        setDeleteModal({ isOpen: false, blogId: null, blogTitle: '' });
        loadUserContent(); // Reload content
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, blogId: null, blogTitle: '' });
  };

  const handleDeleteCaseStudy = async (caseStudyId: number) => {
    if (window.confirm('Are you sure you want to delete this case study?')) {
      try {
        await enhancedBlogService.deleteCaseStudy(caseStudyId);
        loadUserContent(); // Reload content
      } catch (error) {
        console.error('Error deleting case study:', error);
      }
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state if no user data
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-3">Unable to load user data</p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const publishedBlogs = blogs.filter(blog => blog.status === 'published');
  const draftBlogs = blogs.filter(blog => blog.status === 'draft');
  const publishedCaseStudies = caseStudies.filter(cs => cs.status === 'published');
  const draftCaseStudies = caseStudies.filter(cs => cs.status === 'draft');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format signup date from D365
  const formatSignupDate = (dateString: string) => {
    try {
      if (!dateString) return 'Not available';
      
      let date;
      if (typeof dateString === 'string' && dateString.includes('T')) {
        date = new Date(dateString);
      } else if (typeof dateString === 'string' && dateString.includes('/')) {
        date = new Date(dateString);
      } else if (typeof dateString === 'string' && dateString.includes('-')) {
        date = new Date(dateString + 'T00:00:00');
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return 'Date format error';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
    } catch (error) {
      return 'Date parsing error';
    }
  };

  // Format company
  const formatCompany = () => {
    return user.company || 'Company not specified';
  };

  // Check if user has crmonce.com email
  const hasCrmonceEmail = user.email && user.email.includes('crmonce.com');
  
  // Debug log to verify email check
  console.log('Dashboard: User email:', user.email);
  console.log('Dashboard: Has crmonce email:', hasCrmonceEmail);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Dashboard - Manage Your CRMONCE Projects"
        description="Access your CRMONCE dashboard to manage Dynamics 365 and Power Platform projects, view analytics, and connect with our expert consultants."
        keywords="CRMONCE dashboard, project management, Dynamics 365 dashboard, Power Platform projects"
        canonical="/dashboard"
        noIndex={true}
      />
      
      {/* Compact Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Welcome,</span>
                <span className="font-semibold text-blue-600">{user.firstName} {user.lastName}</span>
              </div>
            </div>
            

          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Grid - More Compact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div 
            onClick={() => setShowBlogs(!showBlogs)}
            className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{blogs.length}</p>
                  <p className="text-sm text-gray-600">Total Blogs</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-600 font-medium">{publishedBlogs.length} published</p>
                <p className="text-xs text-gray-500">{draftBlogs.length} drafts</p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => setShowCaseStudies(!showCaseStudies)}
            className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{caseStudies.length}</p>
                  <p className="text-sm text-gray-600">Case Studies</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-600 font-medium">{publishedCaseStudies.length} published</p>
                <p className="text-xs text-gray-500">{draftCaseStudies.length} drafts</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{blogs.reduce((sum, blog) => sum + (blog.views || 0), 0)}</p>
                <p className="text-sm text-gray-600">Total Views</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Activity size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{blogs.reduce((sum, blog) => sum + (blog.likes || 0), 0)}</p>
                <p className="text-sm text-gray-600">Total Likes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Compact - Only for crmonce.com users */}
        {hasCrmonceEmail ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button 
                onClick={() => navigate('/create-blog')}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left border border-blue-200 hover:border-blue-300"
              >
                <Plus size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Create New Blog</span>
              </button>
              
              <button 
                onClick={() => navigate('/create-case-study')}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-green-50 transition-colors text-left border border-green-200 hover:border-green-300"
              >
                <Plus size={18} className="text-green-600" />
                <span className="text-sm font-medium text-gray-900">Create Case Study</span>
              </button>
              
              <button 
                onClick={() => navigate('/blog')}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-purple-50 transition-colors text-left border border-purple-200 hover:border-purple-300"
              >
                <FileText size={18} className="text-purple-600" />
                <span className="text-sm font-medium text-gray-900">View All Content</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 mb-6 text-center">
            <div className="text-gray-500">
              <h3 className="text-lg font-medium mb-2">Content Management Restricted</h3>
              <p className="text-sm">Blog and case study creation is available only for crmonce.com users.</p>
              <p className="text-xs mt-1">Your email: {user.email}</p>
            </div>
          </div>
        )}

        {/* Content Management - Side by Side - Only for crmonce.com users */}
        {hasCrmonceEmail ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Blogs Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FileText size={20} className="text-blue-600" />
                <span>Blog Management</span>
              </h2>
              <button 
                onClick={() => setShowBlogs(!showBlogs)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {showBlogs ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {isLoadingContent ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">Loading blogs...</p>
              </div>
            ) : (
              <div className="p-4">
                {showBlogs && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {blogs.length === 0 ? (
                      <p className="text-center text-gray-500 py-4 text-sm">No blogs created yet</p>
                    ) : (
                      blogs.map((blog) => (
                        <div key={blog.id} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate text-sm">{blog.title}</h3>
                            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                blog.status === 'published' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {blog.status}
                              </span>
                              <span>{formatDate(blog.updatedAt)}</span>
                              {blog.status === 'published' && (
                                <span>{blog.views} views</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button 
                              onClick={() => navigate(`/edit-blog/${blog.slug}`)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            {blog.status === 'draft' && (
                              <button 
                                onClick={() => handlePublishBlog(blog.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="Publish"
                              >
                                <Eye size={14} />
                              </button>
                            )}
                            <button 
                              onClick={() => navigate(`/blog/${blog.slug}`)}
                              className="p-1.5 text-gray-600 hover:bg-gray-50 rounded"
                              title="View"
                            >
                              <ExternalLink size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteBlog(blog.id, blog.title)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Case Studies Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Building size={20} className="text-green-600" />
                <span>Case Study Management</span>
              </h2>
              <button 
                onClick={() => setShowCaseStudies(!showCaseStudies)}
                className="text-green-600 hover:text-green-700 text-sm font-medium"
              >
                {showCaseStudies ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {isLoadingContent ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">Loading case studies...</p>
              </div>
            ) : (
              <div className="p-4">
                {showCaseStudies && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {caseStudies.length === 0 ? (
                      <p className="text-center text-gray-500 py-4 text-sm">No case studies created yet</p>
                    ) : (
                      caseStudies.map((caseStudy) => (
                        <div key={caseStudy.id} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate text-sm">{caseStudy.title}</h3>
                            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                caseStudy.status === 'published' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {caseStudy.status}
                              </span>
                              <span>{formatDate(caseStudy.updatedAt)}</span>
                              {caseStudy.status === 'published' && (
                                <span>{caseStudy.views} views</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button 
                              onClick={() => navigate(`/edit-case-study/${caseStudy.slug}`)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            {caseStudy.status === 'draft' && (
                              <button 
                                onClick={() => handlePublishCaseStudy(caseStudy.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="Publish"
                              >
                                <Eye size={14} />
                              </button>
                            )}
                            <button 
                              onClick={() => navigate(`/case-studies/${caseStudy.slug}`)}
                              className="p-1.5 text-gray-600 hover:bg-gray-50 rounded"
                              title="View"
                            >
                              <ExternalLink size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteCaseStudy(caseStudy.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        ) : null}

        {/* User Info - Compact */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Name</p>
              <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Email</p>
              <p className="font-medium text-gray-900">{user.email}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Phone</p>
              <p className="font-medium text-gray-900">{user.phone || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Company</p>
              <p className="font-medium text-gray-900">{formatCompany()}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Member Since</p>
              <p className="font-medium text-gray-900">{formatDate(user.createdAt.toString())}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Blog Modal */}
      <DeleteBlog
        blogId={deleteModal.blogId || 0}
        blogTitle={deleteModal.blogTitle}
        onDelete={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isOpen={deleteModal.isOpen}
      />
    </div>
  );
};

export default Dashboard; 